import React, { useEffect, useMemo, useState } from 'react'
import cn from 'classnames'

import useText from '../common/hooks/useText'
import { useAdaptConnectorNaming } from '../lib/adaptConnectorNaming'
import { getProductName } from '../common/services/getProductName'
import { notifyError, notifySuccess } from '../common/features/notificationsManager/Notify'
import { NoBorderButton, SLabelText } from '../common/styles/Form'
import { EditIcon } from '../common/icons/edit-icon'
import { IOcppFormInput, useOcppUserCurrentValidation } from '../common/hooks/useOcppFormValidation'
import { useCapiUserCurrentValidation } from '../common/hooks/useCapiFormValidation'
import {
    ChargePoint,
    Connector,
    useChargePointUpdateChargePointUserSettingsMutation,
} from '../common/redux/api/web-api'
import Input from '../common/components/Input'
import { isChargingConnector } from '../lib/findChargingConnector'
import isOnline from '../lib/isOnline'
import {
    SNameContainer,
    SNameField,
    TypeContainer,
    SProtocolSpan,
    SNameWrapper,
} from '../common/components/ComponentTypeLabel'

import BootstrapModalLayout from './BootstrapModalLayout'
import Text from './Text'

export type EditChargePointModalProps = {
    chargePoint: ChargePoint
    close: (result: unknown) => void
    dismiss: (reason?: string) => void
    editChargerName?: boolean
}
export default function EditChargePointModal({
    chargePoint,
    close,
    dismiss,
    editChargerName,
}: EditChargePointModalProps): React.ReactElement {
    const text = useText()
    const adaptConnectorNaming = useAdaptConnectorNaming()

    const [edit, setEdit] = useState(false)
    const availableConnectors = useMemo(() => {
        return chargePoint.connectors
            .filter((c: Connector) => c.connectorFunctionType === 'Charger')
            .sort((a: Connector, b: Connector) => a.connectorId - b.connectorId)
    }, [chargePoint])

    const validationMethods = chargePoint.isOcpp
        ? useOcppUserCurrentValidation(chargePoint)
        : useCapiUserCurrentValidation(chargePoint)

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isValid, isDirty },
    } = validationMethods

    const title = getProductName(chargePoint.productId)

    const [updateChargePointUserSettings, { isLoading: isSaving }] =
        useChargePointUpdateChargePointUserSettingsMutation()

    useEffect(() => {
        reset(chargePoint)
    }, [chargePoint])

    const saveChargePoint = async (formValues: IOcppFormInput) => {
        try {
            await updateChargePointUserSettings({
                chargePointId: chargePoint.id,
                name: formValues.name ?? '',
                userCurrentConnector1: formValues.uc_1,
                userCurrentConnector2: formValues.uc_2,
            }).unwrap()

            notifySuccess(text('msgChargerUpdated'))
        } catch (e) {
            notifyError(text('msgChargePointNotUpdated'))
        } finally {
            close(chargePoint)
        }
    }

    return (
        <BootstrapModalLayout
            size="md"
            title={text('lblChargerInfoModal')}
            actions={[
                {
                    btn: 'btn-secondary',
                    callback: dismiss,
                    label: text('btnCancel'),
                },
                {
                    callback: handleSubmit(saveChargePoint),
                    disabled: !isValid || !isDirty || !isOnline(chargePoint) || chargePoint.ownerReadOnly,
                    loading: isSaving,
                    label: text('btnSave'),
                },
            ]}
        >
            <form>
                <SNameContainer>
                    {edit ? (
                        <SNameWrapper>
                            <Input
                                type="text"
                                id="name"
                                data-testid={'name-input'}
                                required
                                {...register('name')}
                                error={errors.name?.message}
                            />
                        </SNameWrapper>
                    ) : (
                        <div className="flex gap-2 mb-2">
                            <SNameField className={cn({ userapp: true })}>{chargePoint.name}</SNameField>
                            {editChargerName && (
                                <NoBorderButton onClick={() => setEdit(!edit)} title="lblEditChargerName">
                                    <EditIcon />
                                </NoBorderButton>
                            )}
                        </div>
                    )}
                    <TypeContainer data-testid={`chargePointType-${chargePoint.id}`}>
                        <span>{title}</span>
                        <span>•</span>
                        <SProtocolSpan>{chargePoint?.isOcpp ? text('lblOcpp') : text('lblCapiField')}</SProtocolSpan>
                    </TypeContainer>
                    <div className="row">
                        <div className="form-group">
                            {availableConnectors.filter(isChargingConnector).map((connector) => (
                                <div className="col-xs-12" key={connector.connectorId}>
                                    <div className="form-group">
                                        <SLabelText>
                                            <Text
                                                code="lblCurrentLimitWithMaxVoltage"
                                                values={{
                                                    connectorName: adaptConnectorNaming(connector.name),
                                                    installationCurrent:
                                                        connector?.installationCurrent || connector?.factoryCurrent,
                                                }}
                                            />
                                        </SLabelText>
                                        <Input
                                            type="number"
                                            id={`uc_${connector.connectorId}`}
                                            data-testid={'userCurrent-input-' + connector.connectorId}
                                            error={errors[`uc_${connector.connectorId}`]?.message}
                                            {...register(`uc_${connector.connectorId}`)}
                                            disabled={
                                                isSaving ||
                                                (chargePoint.isOcpp && chargePoint.chargePointStatus !== 'Online')
                                            }
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </SNameContainer>
            </form>
        </BootstrapModalLayout>
    )
}
